import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Input, Avatar } from "@realtyflows/ui";
import { createDeal, getClients, getDocTemplates, getTemplateFields, sendTemplate, updateDocTemplate, getPipelines } from "../api";
import CUSTOM_FIELD_CATALOG from "../data/customFieldCatalog";
import { buyerStages as defaultBuyerStages, sellerStages as defaultSellerStages } from "../data/mockData";
import MoneyInput from "../components/MoneyInput";
import DealTypeSelect from "../components/DealTypeSelect";
import SearchSelect from "../components/SearchSelect";
import useResponsive from "../hooks/useResponsive";

// Dynamic stage → template lookup (from DB, respects auto_send toggle)

const timelineOptions = [
  { value: "Immediately", label: "Immediately" },
  { value: "1-3 months", label: "1-3 months" },
  { value: "3-6 months", label: "3-6 months" },
  { value: "6-12 months", label: "6-12 months" },
  { value: "12+ months", label: "12+ months" },
  { value: "Just browsing", label: "Just browsing" },
];

export default function CreateDeal() {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isTablet = responsive.isTablet;
  const [searchParams] = useSearchParams();
  const initialPipeline = searchParams.get("pipeline") || "buyer";
  const initialClientId = searchParams.get("client_id") || "";

  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [contractPrompt, setContractPrompt] = useState(null); // { template, clientName, clientEmail }
  const [contractFields, setContractFields] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [pipelineOptions, setPipelineOptions] = useState(null);

  // Load dynamic pipelines
  useEffect(() => {
    getPipelines().then(data => {
      if (data && data.length > 0) setPipelineOptions(data);
    }).catch(() => {});
  }, []);

  // Get stages for a pipeline slug
  const getStagesForPipeline = (slug) => {
    if (pipelineOptions) {
      const p = pipelineOptions.find(p => p.slug === slug);
      if (p) return p.stages.map(s => s.name);
    }
    return slug === "seller" ? defaultSellerStages : defaultBuyerStages;
  };

  const defaultType = initialPipeline === "seller" ? "Primary Residence Sale" : "Primary Residence Purchase";
  const defaultStages = getStagesForPipeline(initialPipeline);

  const [form, setForm] = useState({
    client_id: initialClientId, contact_name: "", property_address: "", budget: "", timeline: "",
    type: defaultType, notes: "", pipeline_type: initialPipeline, status: defaultStages[0],
    close_date: "", earnest_money_due: "", mutual_acceptance: "", due_diligence: "",
    final_walk_through: "", possession: "", commission: "", commission_type: "percentage",
    agent_split: "", team_split: "", description: "",
  });

  useEffect(() => {
    getClients().then(setContacts).catch(() => {});
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const set = (updates) => setForm((prev) => ({ ...prev, ...updates }));

  const isBuyer = form.pipeline_type !== "seller";
  const stageList = getStagesForPipeline(form.pipeline_type);

  const buildDealPayload = () => {
    const isPct = form.commission_type === "percentage";
    const fmt = (val) => (val ? (isPct ? `${val}%` : `$${val}`) : "");
    const formattedBudget = form.budget ? `$${form.budget}` : "";
    const { commission_type, ...dealData } = form;
    return {
      ...dealData,
      budget: formattedBudget,
      commission: fmt(form.commission),
      agent_split: fmt(form.agent_split),
      team_split: fmt(form.team_split),
    };
  };

  const handleSubmit = async () => {
    if (!form.property_address.trim() && !form.contact_name.trim() && !form.client_id) {
      setToast({ message: "Add a contact or property address", variant: "error" });
      return;
    }

    // Check if this stage has a matching auto-send template
    const pipeline = form.pipeline_type || "buyer";
    const stage = form.status || "Client";

    setSaving(true);
    try {
      const templates = await getDocTemplates(pipeline);
      const tmpl = templates.find(tpl => tpl.stage === stage && tpl.auto_send !== false);
      if (tmpl) {
        // Resolve client info from contacts or form
        const selectedContact = contacts.find(c => String(c.id) === String(form.client_id));
        const clientName = selectedContact?.name || form.contact_name || "";
        const clientEmail = selectedContact?.email || "";
        const budget = form.budget ? `$${form.budget}` : "";

        setContractPrompt({ template: tmpl, clientName, clientEmail, budget });
        setLoadingFields(true);
        setSaving(false);

        // Get template fields without deal_id — pre-fill from form data
        const data = await getTemplateFields(tmpl.id);
        const editable = {};
        data.fields.filter(f => !f.auto && !f.filled).forEach(f => {
          let prefill = f.value || "";
          if (f.key === "property_address" && form.property_address) prefill = form.property_address;
          if (f.key === "buyer_name" && clientName && pipeline === "buyer") prefill = clientName;
          if (f.key === "buyer_email" && clientEmail && pipeline === "buyer") prefill = clientEmail;
          if (f.key === "seller_name" && clientName && pipeline === "seller") prefill = clientName;
          if (f.key === "seller_email" && clientEmail && pipeline === "seller") prefill = clientEmail;
          if (f.key === "client_name" && clientName) prefill = clientName;
          if (f.key === "client_email" && clientEmail) prefill = clientEmail;
          editable[f.key] = prefill;
        });
        setFieldValues(editable);
        setContractFields(data);
        setLoadingFields(false);
        return; // Show modal, don't create deal yet
      }
    } catch { /* template fetch failed, fall through to create deal */ }
    setSaving(false);

    // No template match — create deal and navigate
    setSaving(true);
    try {
      await createDeal(buildDealPayload());
      setToast({ message: "Deal created successfully", variant: "success" });
      setTimeout(() => navigate("/deals", { state: { pipeline: form.pipeline_type } }), 800);
    } catch (err) {
      setToast({ message: err.message || "Failed to create deal", variant: "error" });
      setSaving(false);
    }
  };

  const handleSendContract = async () => {
    if (!contractPrompt) return;
    const { template, clientName: promptName, clientEmail: promptEmail } = contractPrompt;

    // Check missing agent fields (signer fields are filled during signing)
    if (contractFields) {
      const missing = contractFields.fields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer" && !(fieldValues[f.key]?.trim()));
      if (missing.length > 0) {
        setToast({ message: `Please fill in: ${missing.map(f => f.label).join(", ")}`, variant: "error" });
        return;
      }
    }

    const clientEmail = fieldValues.buyer_email || fieldValues.seller_email || promptEmail;
    const clientName = fieldValues.buyer_name || fieldValues.seller_name || promptName;
    const pipeline = form.pipeline_type || "buyer";
    const role = pipeline === "buyer" ? "Buyer" : "Seller";

    if (!clientEmail) {
      setToast({ message: "Client email is required to send documents", variant: "error" });
      return;
    }

    setSendingContract(true);
    try {
      // 1. Create the deal first
      const newDeal = await createDeal(buildDealPayload());

      // 2. Send template for the new deal
      const overrides = {};
      Object.entries(fieldValues).forEach(([k, v]) => { if (v?.trim()) overrides[k] = v.trim(); });

      await sendTemplate(template.id, {
        deal_id: newDeal.id,
        client_id: newDeal.client_id,
        signers: [{ name: clientName, email: clientEmail, role }],
        field_overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      setToast({ message: `Deal created & ${template.name} sent for signing`, variant: "success" });
      setTimeout(() => navigate("/deals", { state: { pipeline: form.pipeline_type } }), 1000);
    } catch (err) {
      setToast({ message: err.message || "Failed to send contract", variant: "error" });
      setSendingContract(false);
    }
  };

  const handleSkipContract = async () => {
    setContractPrompt(null);
    setSaving(true);
    try {
      await createDeal(buildDealPayload());
      setToast({ message: "Deal created successfully", variant: "success" });
      setTimeout(() => navigate("/deals", { state: { pipeline: form.pipeline_type } }), 800);
    } catch (err) {
      setToast({ message: err.message || "Failed to create deal", variant: "error" });
      setSaving(false);
    }
  };

  const handleAddField = async (field, filledBy) => {
    if (!contractPrompt) return;
    const { template } = contractPrompt;
    const existing = template.custom_fields || [];
    const updated = [...existing, { key: field.key, label: field.label, type: field.type, filled_by: filledBy }];
    try {
      await updateDocTemplate(template.id, { custom_fields: updated });
      contractPrompt.template.custom_fields = updated;
      const data = await getTemplateFields(template.id);
      setContractFields(data);
      const editable = {};
      data.fields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer").forEach(f => { editable[f.key] = fieldValues[f.key] || f.value || ""; });
      setFieldValues(editable);
      setAddFieldOpen(false);
    } catch (err) {
      setToast({ message: err.message || "Failed to add field", variant: "error" });
    }
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 820, margin: "0 auto" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: "12px 20px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          animation: "fadeSlide 0.3s ease",
        }}>
          {toast.message}
        </div>
      )}

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <button onClick={() => navigate("/deals")}
          style={{
            display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            cursor: "pointer", color: t.textSecondary, fontSize: 13, fontFamily: font.body, padding: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Pipeline
        </button>
      </div>

      {/* Main card */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: isMobile ? "20px 16px" : "24px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            New Deal
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
            Fill in the deal details below. Only a contact or property address is required.
          </div>
        </div>

        {/* ── Pipeline & Stage ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Pipeline & Stage</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <SearchSelect
              label="Pipeline"
              value={form.pipeline_type}
              onChange={(val) => {
                const pt = val || "buyer";
                const dt = pt === "buyer" ? "Primary Residence Purchase" : pt === "seller" ? "Primary Residence Sale" : "";
                const sl = getStagesForPipeline(pt);
                set({ pipeline_type: pt, type: dt, status: sl[0] });
              }}
              options={pipelineOptions
                ? pipelineOptions.map(p => ({ value: p.slug, label: p.name }))
                : [
                    { value: "buyer", label: "Buyer Pipeline" },
                    { value: "seller", label: "Seller Pipeline" },
                  ]
              }
              allowEmpty={false}
              placeholder="Select pipeline"
            />
            <SearchSelect
              label="Stage"
              value={form.status}
              onChange={(val) => set({ status: val })}
              options={stageList.map((s) => ({ value: s, label: s }))}
              allowEmpty={false}
              placeholder="Select stage"
            />
          </div>
        </div>

        {/* ── Contact & Type ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Contact & Deal Type</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12 }}>
            <SearchSelect
              label="Contact"
              value={form.client_id}
              onChange={(val) => set({ client_id: val, contact_name: "" })}
              options={contacts.map((c) => ({ value: c.id, label: c.name, subtitle: c.email || c.phone || "" }))}
              emptyLabel="-- New contact --"
              placeholder="-- New contact --"
            />
            {!form.client_id && (
              <Input label="New Contact Name" value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} placeholder="John Smith" />
            )}
            <DealTypeSelect
              label="Type"
              value={form.type}
              onChange={(type) => set({ type })}
              pipeline={form.pipeline_type}
            />
          </div>
        </div>

        {/* ── Price ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Price</div>
          <MoneyInput
            value={form.budget}
            onChange={(formatted) => set({ budget: formatted })}
            placeholder="0"
            hint="Enter the listing or purchase price"
          />
        </div>

        {/* ── Key Dates ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Key Dates</div>
          <div style={{ display: "grid", gridTemplateColumns: isBuyer ? (isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr") : (isMobile ? "1fr" : "1fr 1fr"), gap: 10 }}>
            <Input label="Close Date" type="date" value={form.close_date} onChange={(e) => set({ close_date: e.target.value })} />
            {isBuyer && <Input label="Earnest Money Due" type="date" value={form.earnest_money_due} onChange={(e) => set({ earnest_money_due: e.target.value })} />}
            {isBuyer && <Input label="Mutual Acceptance" type="date" value={form.mutual_acceptance} onChange={(e) => set({ mutual_acceptance: e.target.value })} />}
            {isBuyer && <Input label="Due Diligence" type="date" value={form.due_diligence} onChange={(e) => set({ due_diligence: e.target.value })} />}
            {isBuyer && (
              <Input label="Final Walk Through" type="date" value={form.final_walk_through} onChange={(e) => set({ final_walk_through: e.target.value })} />
            )}
            {isBuyer && (
              <Input label="Possession" type="date" value={form.possession} onChange={(e) => set({ possession: e.target.value })} />
            )}
            <SearchSelect
              label="Timeline"
              value={form.timeline}
              onChange={(val) => set({ timeline: val })}
              options={timelineOptions}
              placeholder="Select timeline"
              allowEmpty
              emptyLabel="-- No timeline --"
            />
          </div>
        </div>

        {/* ── Commission & Splits ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>Commission & Splits</div>
            <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: `1.5px solid ${t.border}` }}>
              {["percentage", "flat"].map((ct) => (
                <div key={ct} onClick={() => set({ commission_type: ct, commission: "", agent_split: "", team_split: "" })}
                  style={{
                    padding: "4px 12px", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: font.body,
                    background: form.commission_type === ct ? `${t.emerald}15` : t.surfaceWarm,
                    color: form.commission_type === ct ? t.emerald : t.textMuted,
                    borderRight: ct === "percentage" ? `1px solid ${t.border}` : "none",
                  }}>
                  {ct === "percentage" ? "%" : "$"}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
            <MoneyInput
              label="Commission"
              mode={form.commission_type === "percentage" ? "percent" : "dollar"}
              value={form.commission}
              onChange={(formatted) => set({ commission: formatted })}
              placeholder={form.commission_type === "percentage" ? "3" : "15,000"}
              max={form.commission_type === "percentage" ? 100 : undefined}
              hint={form.commission_type === "percentage" ? "Typical range: 2–3%" : undefined}
              hintVariant={(() => {
                const num = parseFloat(form.commission);
                if (!form.commission || isNaN(num)) return "default";
                if (form.commission_type === "percentage" && num > 6) return "warning";
                if (num > 0) return "success";
                return "default";
              })()}
            />
            <MoneyInput
              label="Agent Split"
              mode={form.commission_type === "percentage" ? "percent" : "dollar"}
              value={form.agent_split}
              onChange={(formatted) => set({ agent_split: formatted })}
              placeholder={form.commission_type === "percentage" ? "70" : "0"}
              max={form.commission_type === "percentage" ? 100 : undefined}
            />
            <MoneyInput
              label="Team Split"
              mode={form.commission_type === "percentage" ? "percent" : "dollar"}
              value={form.team_split}
              onChange={(formatted) => set({ team_split: formatted })}
              placeholder={form.commission_type === "percentage" ? "30" : "0"}
              max={form.commission_type === "percentage" ? 100 : undefined}
            />
          </div>
        </div>

        {/* ── Property ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.body }}>Property</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <Input label="Property Address" value={form.property_address} onChange={(e) => set({ property_address: e.target.value })} placeholder="1234 N Michigan Ave" />
            <div>
              <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Description</div>
              <textarea value={form.description} onChange={(e) => set({ description: e.target.value })}
                placeholder="Add description" rows={2}
                style={{
                  width: "100%", boxSizing: "border-box", background: t.surfaceWarm,
                  border: `1.5px solid ${t.border}`, borderRadius: 6, padding: "8px 10px",
                  color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body, resize: "vertical",
                }} />
            </div>
          </div>
        </div>

        {/* ── Actions ── */}
        <div style={{ padding: isMobile ? "16px 16px" : "20px 28px", display: "flex", gap: 10, justifyContent: isMobile ? "stretch" : "flex-end", flexDirection: isMobile ? "column-reverse" : "row" }}>
          <Button variant="outline" size="sm" onClick={() => navigate("/deals")} style={isMobile ? { width: "100%" } : undefined}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={saving} style={isMobile ? { width: "100%" } : undefined}>
            {saving ? "Creating..." : "Create Deal"}
          </Button>
        </div>
      </div>

      {/* Contract Prompt Modal */}
      {contractPrompt && (() => {
        const allFields = contractFields?.fields || [];
        const agentMissing = allFields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer");
        const signerFields = allFields.filter(f => f.filled_by === "signer");
        const filledFields = allFields.filter(f => f.filled && f.filled_by !== "signer");
        const allReady = contractFields?.ready || (agentMissing.length === 0 && !loadingFields);

        return (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 998,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeSlide 0.2s ease",
          }} onClick={handleSkipContract}>
            <div style={{
              background: t.surface, borderRadius: 14, width: isMobile ? "calc(100vw - 32px)" : 420, maxWidth: 420, maxHeight: "85vh", overflow: "auto",
              boxShadow: "0 16px 48px rgba(0,0,0,0.3)", border: `1px solid ${t.border}`,
            }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ padding: "18px 22px 14px", borderBottom: `1px solid ${t.border}`, position: "sticky", top: 0, background: t.surface, zIndex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                  {contractPrompt.template.name}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 3 }}>
                  {loadingFields ? "Checking contract fields..." : allReady ? "All fields ready — send when ready" : `${agentMissing.length} field${agentMissing.length !== 1 ? "s" : ""} need${agentMissing.length === 1 ? "s" : ""} your input`}
                </div>
              </div>

              {loadingFields ? (
                <div style={{ padding: "32px 22px", textAlign: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                  Loading fields...
                </div>
              ) : (
                <div style={{ padding: "16px 22px" }}>
                  {/* Client card */}
                  <div style={{
                    background: t.surfaceWarm, borderRadius: 8, padding: "10px 12px",
                    border: `1px solid ${t.border}`, marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <Avatar name={contractPrompt.clientName || "?"} size={30} />
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{contractPrompt.clientName || "New Client"}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{contractPrompt.clientEmail || "No email"}</div>
                    </div>
                    {contractPrompt.budget && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>{contractPrompt.budget}</div>
                    )}
                  </div>

                  {/* Missing agent fields — editable */}
                  {agentMissing.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.coral, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        REQUIRED FIELDS
                      </div>
                      {agentMissing.map(f => (
                        <div key={f.key} style={{ marginBottom: 10 }}>
                          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: t.textSecondary, marginBottom: 4, fontFamily: font.body }}>
                            {f.label}
                            {f.custom && <span style={{ fontSize: 8, color: t.sky, marginLeft: 6, fontWeight: 700, letterSpacing: 0.3 }}>CRM</span>}
                          </label>
                          <input
                            type={f.type === "date" ? "date" : f.type === "money" ? "number" : "text"}
                            value={fieldValues[f.key] || ""}
                            onChange={e => setFieldValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={f.type === "money" ? "0" : f.label}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 12,
                              background: t.surfaceWarm, border: `1.5px solid ${t.coral}30`,
                              borderRadius: 6, color: t.textPrimary, outline: "none", fontFamily: font.body,
                            }}
                            onFocus={e => e.target.style.borderColor = t.emerald}
                            onBlur={e => e.target.style.borderColor = `${t.coral}30`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Filled fields — read-only summary */}
                  {filledFields.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.emerald, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        AUTO-FILLED
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
                        {filledFields.map(f => (
                          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 9, color: t.emerald }}>&#10003;</span>
                            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                            {f.custom && <span style={{ fontSize: 7, color: t.sky, fontWeight: 700 }}>CRM</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signer fields — filled during signing */}
                  {signerFields.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.amber, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        FILLED BY SIGNER
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
                        {signerFields.map(f => (
                          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round"><path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Field */}
                  {(() => {
                    const usedKeys = new Set(allFields.map(f => f.key));
                    const available = CUSTOM_FIELD_CATALOG.filter(f => !usedKeys.has(f.key));
                    if (available.length === 0) return null;
                    return (
                      <div style={{ marginBottom: 12, position: "relative" }}>
                        <div
                          onClick={() => setAddFieldOpen(!addFieldOpen)}
                          style={{
                            display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                            fontSize: 10, fontWeight: 600, color: t.sky, fontFamily: font.body,
                            padding: "6px 0",
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 0.7}
                          onMouseLeave={e => e.currentTarget.style.opacity = 1}
                        >
                          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add CRM Field
                        </div>
                        {addFieldOpen && (
                          <div style={{
                            background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", maxHeight: 200, overflowY: "auto",
                            position: "absolute", left: 0, right: 0, zIndex: 5,
                          }}>
                            {available.map(f => (
                              <div key={f.key} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "8px 12px", borderBottom: `1px solid ${t.border}`,
                                fontSize: 11, fontFamily: font.body,
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ color: t.textPrimary, fontWeight: 500 }}>{f.label}</span>
                                  <span style={{ fontSize: 8, color: t.textMuted, background: t.surfaceWarm, padding: "1px 4px", borderRadius: 3 }}>{f.type}</span>
                                </div>
                                <div style={{ display: "flex", gap: 4 }}>
                                  <div onClick={() => handleAddField(f, "agent")}
                                    style={{ fontSize: 9, fontWeight: 700, color: t.emerald, cursor: "pointer", padding: "2px 6px", borderRadius: 4, background: `${t.emerald}10`, border: `1px solid ${t.emerald}20` }}
                                    onMouseEnter={e => e.currentTarget.style.background = `${t.emerald}25`}
                                    onMouseLeave={e => e.currentTarget.style.background = `${t.emerald}10`}
                                  >Agent</div>
                                  <div onClick={() => handleAddField(f, "signer")}
                                    style={{ fontSize: 9, fontWeight: 700, color: "#F59E0B", cursor: "pointer", padding: "2px 6px", borderRadius: 4, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(245,158,11,0.2)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "rgba(245,158,11,0.1)"}
                                  >Signer</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Signing flow */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: `${t.sky}10`, borderRadius: 5, border: `1px solid ${t.sky}20` }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: t.sky, color: "#fff", fontSize: 7, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>1</div>
                      <span style={{ fontSize: 10, color: t.textPrimary, fontFamily: font.body }}>You sign</span>
                    </div>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: `${t.emerald}10`, borderRadius: 5, border: `1px solid ${t.emerald}20` }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: t.emerald, color: "#fff", fontSize: 7, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>2</div>
                      <span style={{ fontSize: 10, color: t.textPrimary, fontFamily: font.body }}>Client signs</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{
                padding: "14px 22px 18px", display: "flex", gap: 10, justifyContent: "flex-end",
                borderTop: `1px solid ${t.border}`, position: "sticky", bottom: 0, background: t.surface,
              }}>
                <Button variant="outline" size="sm" onClick={handleSkipContract}>
                  Skip
                </Button>
                <Button variant="primary" size="sm" onClick={handleSendContract} disabled={sendingContract || loadingFields}>
                  {sendingContract ? "Sending..." : "Send for Signing"}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
